<template>
    <div class="video-actions">
        <BaseButton
            class="base-button--outline"
            :class="{ 'base-button--active': isFavorited && !loadingFavoriteToggle }"
            :loading="loadingFavoriteToggle"
            :icon-left="isFavorited ? 'favorite-filled' : 'favorite-outline'"
            gtm-video-favorite
            @click="handleFavorite"
        >
            Favourite
        </BaseButton>
        <BaseButton
            v-if="canShare"
            class="base-button--outline"
            icon-left="call-made"
            gtm-video-share
            @click="handleShare"
        >
            Share
        </BaseButton>
        <BaseButton
            class="base-button--outline"
            icon-left="qr"
            gtm-video-qr
            @click="handleQR"
        >
            QR
        </BaseButton>
        <BaseButton
            class="base-button--outline"
            icon-left="content-copy"
            gtm-video-cite
            @click="handleCite"
        >
            Cite
        </BaseButton>
    </div>
</template>

<script setup>
import ToggleFavoriteVideoMutation from '~/graphql/mutations/toggleFavoriteVideo.graphql';
import { useModalStore, MODAL_QR, MODAL_CITE, MODAL_LOGIN } from '~/store/modal';
import { useToastStore } from '~/store/toast';
import { useAuthStore } from '~/store/auth';

const props = defineProps({
    video: {
        type: Object,
        default: null,
        required: false
    }
});

const { execute: toggleFavoriteVideoExecute } = useAsyncMutation('toggle-favorite-video');
const modalStore = useModalStore();
const authStore = useAuthStore();
const toastStore = useToastStore();

const loadingFavoriteToggle = ref(false);

const canShare = computed(() => {
    return navigator?.share !== undefined;
});

const isFavorited = computed(() => {
    if (!authStore.loggedIn) {
        return false;
    }

    return authStore.favoriteVideos.includes(props.video.id);
});

async function handleFavorite() {
    if (!authStore.loggedIn) {
        modalStore.toggleModal(MODAL_LOGIN);
        return;
    }

    if (!props.video) {
        toastStore.add('An error occurred', 3000, 'error');
        return;
    }

    loadingFavoriteToggle.value = true;
    const { data, error } = await toggleFavoriteVideoExecute({
        mutation: ToggleFavoriteVideoMutation,
        variables: {
            videoId: props.video.id
        }
    });
    loadingFavoriteToggle.value = false;

    if (
        error.value ||
        data?.value?.data?.toggleFavoriteVideo?.status === 'error'
    ) {
        toastStore.add('An error occurred', 3000, 'error');
        return;
    }

    const response = data?.value?.data?.toggleFavoriteVideo;
    if (
        response?.status === 'ok' &&
        response?.message
    ) {
        if (response?.message === 'VIDEO_FAVORITED') {
            authStore.favoriteVideos.push(props.video.id);
            toastStore.add('Video added to favourites', 4000, 'check');
        }

        if (response?.message === 'VIDEO_UNFAVORITED') {
            authStore.favoriteVideos.splice(authStore.favoriteVideos.indexOf(props.video.id), 1);
            toastStore.add('Video removed from favourites', 4000, 'check');
        }
    }
}

function handleShare() {
    navigator.share({
        url: window.location.href
    });
}

function handleQR() {
    if (authStore.loggedIn) {
        modalStore.toggleModal(MODAL_QR);
    } else {
        modalStore.toggleModal(MODAL_LOGIN);
    }
}

function handleCite() {
    if (authStore.loggedIn) {
        modalStore.toggleModal(MODAL_CITE);
    } else {
        modalStore.toggleModal(MODAL_LOGIN);
    }
}
</script>

<style lang="less">
.video-actions {
    position: relative;
    display: flex;
    align-items: start;
    gap: var(--spacing-xs);
    overflow: auto;

    @media @q-xl-max {
        margin-top: var(--spacing-md);
    }

    .dn-button.base-button {
        @media @q-md-min {
            .typography-label-xs();
            padding: .375rem .625rem;

            .dn-icon {
                font-size: 1rem;
            }
        }
    }

    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}
</style>
